import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { useAssetInlineBlob, useAssets, useTrackedDownloadHandler } from "@/hooks/use-assets";
import type { Asset } from "@/stores/asset-store";
import { ChevronDown, Download, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

function StatementCard({
  statement,
  defaultOpen,
  onDownload,
}: {
  statement: Asset;
  defaultOpen: boolean;
  onDownload: (statement: Asset) => void;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // Fetch the PDF only once the preview is expanded
  const inlineBlobQuery = useAssetInlineBlob(isOpen ? statement.asset_path : undefined);
  const blob = inlineBlobQuery.data;
  // Object URLs are disposable browser resources — create them in an effect
  // (never during render) and revoke that exact URL in the cleanup.
  const [previewUrl, setPreviewUrl] = useState<string>();
  useEffect(() => {
    if (!blob) {
      setPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CollapsibleTrigger className="flex flex-1 cursor-pointer items-center gap-2 text-left">
              <ChevronDown className={`size-4 shrink-0 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
              <CardTitle className="flex items-center gap-2">
                {statement.asset_name}
                <Badge variant="secondary" className="font-mono">
                  {statement.calendar_version}
                </Badge>
              </CardTitle>
            </CollapsibleTrigger>
            <Button variant="outline" size="sm" onClick={() => onDownload(statement)}>
              <Download />
              Download
            </Button>
          </div>
          {statement.asset_description && <CardDescription>{statement.asset_description}</CardDescription>}
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            {inlineBlobQuery.isLoading && <Skeleton className="h-[70vh] w-full" />}
            {/* No sandbox on the iframe: Chromium's PDF viewer does not render inside
                sandboxed frames (blank sad-document icon). Safe because the backend
                /view proxy serves only PDFs inline — any other stored type comes back
                as an attachment with nosniff, so active content never reaches this frame. */}
            {previewUrl && (
              <iframe
                src={previewUrl}
                title={`${statement.asset_name} ${statement.calendar_version}`}
                className="h-[70vh] w-full rounded-md border"
              />
            )}
            {inlineBlobQuery.isError && (
              <p className="text-sm text-muted-foreground">
                The statement could not be displayed inline. Use the download button to view it.
              </p>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export function SecurityStatementsView() {
  const assetsQuery = useAssets();
  const trackedDownload = useTrackedDownloadHandler();

  const statements = useMemo(() => {
    const list = assetsQuery.data?.securityStatements ?? [];
    // Newest first; calendar_version is YYYY.MM.DD so string compare sorts correctly
    return [...list].sort((a, b) => b.calendar_version.localeCompare(a.calendar_version));
  }, [assetsQuery.data?.securityStatements]);

  const latest = statements[0];

  const handleDownload = (statement: Asset) => trackedDownload(statement, statement.calendar_version);

  if (assetsQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  if (assetsQuery.isError) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Security Statements</CardTitle>
            <CardDescription>Security statements could not be loaded. Try again later.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (statements.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Security Statements</CardTitle>
            <CardDescription>No security statements are available yet.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <ShieldCheck className="size-8" />
            Security Statements
          </h1>
          <p className="text-muted-foreground">
            Istari Digital Platform vulnerability disclosure and exploitability assessments
          </p>
        </div>
        {latest && (
          <Button onClick={() => handleDownload(latest)}>
            <Download />
            Download Latest
          </Button>
        )}
      </div>

      {statements.map((statement) => (
        <StatementCard
          key={statement.asset_path}
          statement={statement}
          defaultOpen={statement === latest}
          onDownload={handleDownload}
        />
      ))}
    </div>
  );
}
